/**
 * **The closed-form attack EV toolkit** — P(hit)/P(crit)/E[damage] for a single weapon attack, composed from `probability.ts`'s pure
 * combinatorics plus the engine's own exported primitives (`terrainVisibility` for cover,
 * `mitigate`/`foldedResistance`/`isPhysical` for the damage pipeline, `isRangedAttack`/
 * `isMeleeActionAttack` for the cover-eligibility gate that mirrors `foldAttackCover`'s (`resolve/attackModifiers.ts`),
 * `foldModifiers`/`attackPredicateContext`/`seedModifiers`/`seedWeaponModifiers`/
 * `seedPilotModifiers`/`gatherConferredModifiers` for the overlay-modifier fold). No dice are
 * rolled; every quantity is exact given the state.
 *
 * **What is folded:** cover, Smart, Invisible, Resistance, plus the general overlay-modifier
 * system the resolver runs — composing the SAME `foldModifiers`/`attackPredicateContext`
 * primitives (`effects/modifiers.ts`) wholesale (never re-deriving the modifier math) exactly the way
 * `resolveSingleTarget` gathers them: the attacker's status + weapon-tag + pilot-effect +
 * adjacent-ally-conferred modifiers, and the target's status + pilot-effect +
 * adjacent-ally-conferred modifiers. That nets **Impaired/Lock On/Braced's** accuracy facets,
 * **Stunned's** Evasion cap, and **Exposed's** physical-damage doubling.
 *
 * **Still NOT folded** (a bounded, deliberate scope): Shredded's armor-ignore, a defender's live
 * Brace/interception reaction (a
 * REACTION, not a passive modifier — out of this toolkit's closed-form shape entirely), and
 * `overrideDamageType` and the roll replacements (advantage, maximize), whose d20/damage-instance
 * shape this toolkit doesn't model. Riders (`weapon.riders`) and `variable`
 * damage type resolution are likewise out of scope — see
 * {@link EvaluateAttackArgs.damageTypeOverride}.
 */

import {
  hasStatus,
  INVISIBLE_STATUS,
  terrainVisibility,
  worldFor,
  type Cell,
  type Combatant,
  type DamageType,
  type EncounterState,
  type WeaponProfile,
} from "@lancer-vtt/domain";
import {
  attackPredicateContext,
  combatantEffectIds,
  foldedResistance,
  foldModifiers,
  gatherConferredModifiers,
  grapplePartner,
  isMeleeActionAttack,
  isPhysical,
  isRangedWeaponAttack,
  mitigate,
  seedModifiers,
  seedPilotModifiers,
  seedWeaponModifiers,
  SEED_OVERLAY,
  alliedCoverCells,
  type LoadedOverlay,
} from "@lancer-vtt/rules";
import { damageDicePool, expectedOverkillHeat, expectedValue, hitCritProbabilities, topNDiceSumDistribution } from "./probability";

export type AttackEvArgs = {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  /** The already-resolved weapon profile (loadout entry or a melee-action synthetic profile). */
  weapon: WeaponProfile;
  /** The weapon's own `weapon` ref, as `checkAttackLegal`/the resolver would key it (`act_ram`, an instance key, …) — drives the cover/melee-action gate. */
  weaponRef: string;
  /** Net accuracy/difficulty declared (mirrors `RequestAttack.accuracy`); default 0. */
  accuracy?: number;
  /** Required when `weapon.damage.type === "variable"` — this toolkit does not model the resolver's `variable`-type decision prompt. */
  damageTypeOverride?: DamageType;
  overlay?: LoadedOverlay;
};

export type AttackEv = {
  pHit: number;
  pCrit: number;
  pMiss: number;
  cover: "none" | "soft" | "hard";
  /** Overall E[final, mitigated damage] across the miss(+Reliable)/hit/crit outcomes. */
  expectedDamage: number;
  /** E[damage | it was a hit or crit] — 0 if `pHit === 0`. */
  expectedDamageGivenHit: number;
  /** E[Overkill reroll heat] — 0 for a non-Overkill weapon. Unconditional on hit (Overkill heat accrues on every fired shot's damage roll, `resolve/damage.ts`'s `rollInstanceDice`), so this is folded in only when a hit/crit actually rolls damage: `pHit * E[heat]`. */
  expectedOverkillHeat: number;
};

/** The raw to-hit inputs `hitCritProbabilities` was called with — `search.ts`'s chance-node bucketing needs these (not just the folded pHit/pCrit) to pick a CONCRETE d20 (+ accuracy-die) realization that lands in a chosen bucket, without re-deriving the cover/Smart/defense/overlay-modifier math a second time. */
export type ToHitInputs = {
  bonus: number;
  /** Net accuracy/difficulty actually fed to `hitCritProbabilities` (the caller's `accuracy` plus the cover fold plus the attacker's + target's overlay-modifier accuracy facets — Impaired/Lock On/Braced) — positive accuracy, negative difficulty, 0 none. */
  netAcc: number;
  /** The target's defense (Evasion, Stunned's `evasionCap`-capped, or E-DEFENSE for a Smart weapon). */
  defense: number;
  noCrits: boolean;
  cover: "none" | "soft" | "hard";
  /** Exposed: true iff a target-side overlay modifier doubles physical (kinetic/energy/explosive) damage against it — folded alongside the accuracy math so `evaluateAttackEv`'s damage side and `search.ts`'s chance-node realization read the SAME fold, never a second derivation. */
  doubleDamage: boolean;
};

/**
 * Fold the attacker's + target's overlay modifiers exactly the way `resolveSingleTarget`'s S3
 * stage (`resolve/attackModifiers.ts`) gathers them for the to-hit fold (D3/D7/D8/P4/P5) — status modifiers
 * (`seedModifiers`) + the attacker's weapon-tag modifiers (`seedWeaponModifiers`) + each side's
 * frozen pilot-effect modifiers (`seedPilotModifiers`, threaded with its own bound Grapple
 * partner) + whatever an adjacent same-side ally confers onto it (`gatherConferredModifiers`).
 * Composes the resolver's OWN fold primitive (`foldModifiers`/`attackPredicateContext`, `effects/modifiers.ts`)
 * — never re-derives the modifier math. Predicate-context facts a real attack resolution also
 * threads (`rangedAttack`/`weaponType`/`mount`/`firstAttackThisTurn`/`chosen`) are omitted here,
 * same as every other non-attack `attackPredicateContext` caller (saves, contests) — an omitted
 * fact just leaves a condition keyed on it dark, per that seam's own documented contract, and no
 * folded modifier in this toolkit's scope (Impaired/Lock On/Braced/Stunned/Exposed) carries a
 * `condition` at all.
 */
function foldOverlayModifiers(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile; overlay: LoadedOverlay }) {
  const { state, attacker, target, weapon, overlay } = args;
  const pctx = attackPredicateContext({ state, attacker: attacker.id, target: target.id });
  const atkMods = foldModifiers(
    [
      ...seedModifiers(state, attacker.id, overlay),
      ...seedWeaponModifiers(attacker.id, weapon, overlay),
      ...seedPilotModifiers({
        carrierId: attacker.id,
        pilotEffectIds: combatantEffectIds(attacker),
        overlay,
        partner: grapplePartner(state, attacker.id),
      }),
      ...gatherConferredModifiers(state, attacker.id, overlay),
    ],
    "attacker",
    pctx,
  );
  const tgtMods = foldModifiers(
    [
      ...seedModifiers(state, target.id, overlay),
      ...seedPilotModifiers({
        carrierId: target.id,
        pilotEffectIds: combatantEffectIds(target),
        overlay,
        partner: grapplePartner(state, target.id),
      }),
      ...gatherConferredModifiers(state, target.id, overlay),
    ],
    "target",
    pctx,
  );
  return { atkMods, tgtMods };
}

/** The bonus/netAcc/defense/noCrits/cover/doubleDamage a to-hit roll for this attack would use — shared by {@link evaluateAttackEv} and `search.ts`'s chance-node dice-forcing (exported so the latter never re-derives the cover/Smart/defense/overlay-modifier fold). */
export function toHitInputsFor(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile; weaponRef: string; accuracy?: number; overlay?: LoadedOverlay }): ToHitInputs {
  const { state, attacker, target, weapon, weaponRef, accuracy = 0, overlay = SEED_OVERLAY } = args;
  const cover = coverBetween({ state, attacker, target, weapon, weaponRef, overlay });
  const coverAcc = cover === "soft" ? -1 : cover === "hard" ? -2 : 0;
  const bonus = weapon.attackBonus ?? attacker.base.attackBonus ?? 0;
  const isSmart = weapon.smart ?? false;
  const rawDefense = isSmart ? (target.base.edef <= 0 ? 8 : target.base.edef) : target.base.evasion;

  const { atkMods, tgtMods } = foldOverlayModifiers({ state, attacker, target, weapon, overlay });
  const overlayAcc = atkMods.attackerAccuracy + tgtMods.targetAccuracy;
  // Stunned's Evasion cap (`resolve/toHit.ts`'s `cappedEvasion`) applies to a weapon attack's Evasion
  // check only — a Smart attack still checks the target's E-DEFENSE, untouched by this facet
  // (mirrors `defenseFor`'s own `tech ? edef : isSmart ? edef... : cappedEvasion` branch, `resolve/toHit.ts`).
  const defense = !isSmart && tgtMods.evasionCap !== undefined ? Math.min(rawDefense, tgtMods.evasionCap) : rawDefense;
  const netAcc = accuracy + coverAcc + overlayAcc;
  return { bonus, netAcc, defense, noCrits: attacker.base.noCrits ?? false, cover, doubleDamage: tgtMods.doubleDamage };
}

/** `weapon.damage.type` resolved to a concrete {@link DamageType}, honoring an explicit override. Throws on an unresolved `variable` — the caller must supply {@link AttackEvArgs.damageTypeOverride}. */
function resolveDamageType(weapon: WeaponProfile, override: DamageType | undefined): DamageType {
  if (weapon.damage.type !== "variable") return weapon.damage.type;
  if (override) return override;
  throw new Error(`ai: weapon "${weapon.name}" has a "variable" damage type — evaluateAttackEv needs a damageTypeOverride (v1 doesn't model the resolver's variable-type decision prompt)`);
}

/** The cover degree between attacker and target — mirrors `foldAttackCover`'s gate (`resolve/attackModifiers.ts`) exactly (ranged-or-Thrown, not Seeking, not a melee-action synthetic attack, not tech). */
function coverBetween(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile; weaponRef: string; overlay: LoadedOverlay }): "none" | "soft" | "hard" {
  const { state, attacker, target, weapon, weaponRef, overlay } = args;
  const isSeeking = weapon.tags?.some((t) => t.id === "tg_seeking") ?? false;
  const forcesCover = weapon.tags?.some((t) => t.id === "tg_thrown") ?? false;
  const isActionAttack = isMeleeActionAttack(weaponRef);
  // An either-mode weapon (the Ram Cannon) is melee against an adjacent-enough target — the same
  // default `resolve()` applies when the intent names no `mode`.
  const ranged = isRangedWeaponAttack({ state, attacker, intent: { target: { kind: "unit", id: target.id } }, weapon });
  if (isActionAttack || isSeeking || !(ranged || forcesCover)) return "none";
  if (attacker.pos == null || target.pos == null) return "none";
  const world = worldFor(state);
  const originFp = world.footprint(attacker.pos, attacker.base.size).cells();
  const targetFp = world.footprint(target.pos, target.base.size).cells();
  if (originFp.length === 0 || targetFp.length === 0) return "none";
  const view = terrainVisibility({
    space: world,
    terrain: state.terrain,
    from: originFp,
    to: targetFp,
    edges: state.edges,
    combatants: state.combatants,
    excludeCoverFrom: target.id,
    areaEffects: state.areaEffects,
    extraHardCoverCells: alliedCoverCells({ state, targetId: target.id, attackerId: attacker.id, overlay }),
  });
  return view.cover;
}

/** E[final mitigated damage] for one damage instance rolled with `isCrit`/`overkill`, plus its own expected heat contribution. `doublePhysical` mirrors `resolve/damageApply.ts`'s own Exposed fold: the WHOLE pre-mitigation total (dice sum + bonus) doubles for a physical (`isPhysical`) damage type, folded before `mitigate`, never after. */
function evaluateDamageInstance(args: {
  count: number;
  sides: number;
  bonus: number;
  isCrit: boolean;
  overkill: boolean;
  dtype: DamageType;
  ap: boolean;
  targetArmor: number;
  resistant: boolean;
  doublePhysical: boolean;
  /** Reliable X — a hit's rolled total never falls below it (`resolve/damageApply.ts`). */
  floor: number;
}): { expectedFinal: number; expectedHeat: number } {
  const { count, sides, bonus, isCrit, overkill, dtype, ap, targetArmor, resistant, doublePhysical, floor } = args;
  const doubles = doublePhysical && isPhysical(dtype);
  if (count === 0) {
    const raw = Math.max(bonus, floor);
    const preMitigation = doubles ? raw * 2 : raw;
    const mit = mitigate({ preMitigation, dtype, ap, targetArmor, resistant });
    return { expectedFinal: mit.final, expectedHeat: 0 };
  }
  const { pool, loFace } = damageDicePool(count, isCrit, overkill);
  const dist = topNDiceSumDistribution({ m: pool, n: count, loFace, hiFace: sides });
  let expectedFinal = 0;
  for (const [sum, p] of dist) {
    const raw = Math.max(sum + bonus, floor);
    const preMitigation = doubles ? raw * 2 : raw;
    const mit = mitigate({ preMitigation, dtype, ap, targetArmor, resistant });
    expectedFinal += p * mit.final;
  }
  const expectedHeat = overkill ? expectedOverkillHeat(pool, sides) : 0;
  return { expectedFinal, expectedHeat };
}

/**
 * The closed-form EV of a single weapon attack — the toolkit's one entry point. Composes:
 * `hitCritProbabilities` (cover/overlay-folded net accuracy vs Evasion/E-DEFENSE — Stunned's
 * evasion cap included, Smart's defense swap, Invisible's pre-roll miss chance) +
 * `evaluateDamageInstance` (exact top-N dice-sum distribution through `mitigate`, honoring
 * `foldedResistance` and Exposed's physical-damage doubling) + Reliable's floor, on a hit and on a miss.
 */
export function evaluateAttackEv(args: AttackEvArgs): AttackEv {
  const { state, attacker, target, weapon, weaponRef, accuracy = 0, damageTypeOverride, overlay = SEED_OVERLAY } = args;
  const dtype = resolveDamageType(weapon, damageTypeOverride);
  const { bonus, netAcc, defense, noCrits, cover, doubleDamage } = toHitInputsFor({ state, attacker, target, weapon, weaponRef, accuracy, overlay });

  const invisibleMissChance = hasStatus(target.statuses, INVISIBLE_STATUS) ? 0.5 : 0;
  const roll = hitCritProbabilities({ bonus, netAcc, defense, noCrits });
  const pHit = (1 - invisibleMissChance) * roll.pHit;
  const pCrit = (1 - invisibleMissChance) * roll.pCrit;
  const pMiss = 1 - pHit;

  const resistant = foldedResistance({ state, targetId: target.id, attackerId: attacker.id, fromAttack: true, dtype, overlay });
  const targetArmor = target.base.armor;

  const damage = weapon.damage;
  const hitOutcome = evaluateDamageInstance({ count: damage.count, sides: damage.sides, bonus: damage.bonus, isCrit: false, overkill: weapon.overkill, dtype, ap: weapon.ap, targetArmor, resistant, doublePhysical: doubleDamage, floor: weapon.reliable });
  const critOutcome = damage.count === 0
    ? hitOutcome
    : evaluateDamageInstance({ count: damage.count, sides: damage.sides, bonus: damage.bonus, isCrit: true, overkill: weapon.overkill, dtype, ap: weapon.ap, targetArmor, resistant, doublePhysical: doubleDamage, floor: weapon.reliable });

  const pPlainHit = Math.max(0, pHit - pCrit);
  const reliablePreMitigation = doubleDamage && isPhysical(dtype) ? weapon.reliable * 2 : weapon.reliable;
  const reliableFinal = weapon.reliable > 0 ? mitigate({ preMitigation: reliablePreMitigation, dtype, ap: weapon.ap, targetArmor, resistant }).final : 0;

  const expectedDamage = pCrit * critOutcome.expectedFinal + pPlainHit * hitOutcome.expectedFinal + pMiss * reliableFinal;
  const expectedDamageGivenHit = pHit > 0 ? (pCrit * critOutcome.expectedFinal + pPlainHit * hitOutcome.expectedFinal) / pHit : 0;
  const expectedOverkillHeatTotal = pCrit * critOutcome.expectedHeat + pPlainHit * hitOutcome.expectedHeat;

  return { pHit, pCrit, pMiss, cover, expectedDamage, expectedDamageGivenHit, expectedOverkillHeat: expectedOverkillHeatTotal };
}

/** Re-exported for callers that need cell/world plumbing alongside the EV toolkit (advisor.ts). */
export type { Cell };
